import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useModalManager } from "shared/hooks/useModalManager";
import EditProductsModal from "./components/EditProductModal";
import DeleteProductModal from "./components/DeleteProductModal";
import Toolbar from "./components/ProductToolbar";
import ProductTable from "./components/ProductTable";
import ScrollToTopButton from "shared/ui/scrolltotop";
import EmptyStateComponent from "shared/ui/empty";
import { deleteProduct, fetchProducts, updateProduct } from "./api/productsApi";
import { getMerchantCategories } from "features/categories/api/categoriesApi";
import { toast } from "react-toastify";
import { useScrollToTop } from "shared/hooks/useScrollToTop";

// Нэг хуудсанд татах мөр. Backend дээд тал нь 200-г зөвшөөрнө.
// 32 байхад Хархираагийн 2,902 бараа = 91 хуудас байсан.
const PAGE_SIZE = 100;

/** Модноос `id`-тай зангилааг (ямар ч гүнээс) олно. */
function findNode(nodes, id) {
  for (const n of nodes || []) {
    if (String(n.id) === String(id)) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
}

/** Зангилаа + түүний БҮХ үр сад (ангилал 2-оос олон давхар байж болно). */
function collectIds(node) {
  if (!node) return [];
  return [
    Number(node.id),
    ...(node.children || []).flatMap((c) => collectIds(c)),
  ];
}

/** id давхардлыг арилгана — хуудасны зааг бараа устахад гулсаж болно. */
function dedupeById(list) {
  const seen = new Set();
  const out = [];
  for (const p of list) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    out.push(p);
  }
  return out;
}

export default function MerchantProductsEditScreen() {
  const { closeModal, currentModal, openModal } = useModalManager();
  const { showScrollButton, scrollToTop } = useScrollToTop();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false); // эхний / шинэчлэх татлага
  const [loadingMore, setLoadingMore] = useState(false); // цааш ачаалах
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // ⚠️ `page`-ийг state-д хадгалбал `handleLoadMore`-ийн closure хуучирч
  //    нэг хуудсыг давхар татдаг — ref нь үргэлж сүүлийн утгыг хардаг.
  const pageRef = useRef(1);
  // Хуучирсан хариуг таних — хайлт бичих үед 3-4 хүсэлт зэрэг явж, буруу
  // дарааллаар ирвэл өмнөх үсгийн үр дүн үлддэг байв.
  const reqIdRef = useRef(0);
  const inFlightRef = useRef(false);

  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(null); // parent
  const [selectedSubCategory, setSelectedSubCategory] = useState(null); // child
  // null = бүгд · "true" = зөвхөн харагдах · "false" = зөвхөн нуугдсан
  const [visibility, setVisibility] = useState(null);

  const [loadingProductId, setLoadingProductId] = useState();
  const [saving, setSaving] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [rawCategories, setRawCategories] = useState([]); // tree
  const [categories, setCategories] = useState([
    { value: null, label: "Бүх ангилал" },
  ]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      setCategoriesError(null);

      const res = await getMerchantCategories();
      if (!res?.success) throw new Error(res?.message || "Ангилал татаж чадсангүй");

      const tree = Array.isArray(res.data) ? res.data : [];
      setRawCategories(tree);

      const categoryOptions = tree.map((cat) => ({
        value: String(cat.id),
        label: cat.name,
      }));
      setCategories([{ value: null, label: "Бүх ангилал" }, ...categoryOptions]);
    } catch (error) {
      console.error("Fetch categories error:", error);
      setCategoriesError("Ангилал татахад алдаа гарлаа.");
      setCategories([{ value: "", label: "Бүх ангилал" }]);
      setRawCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // ⚠️ ЭНЭ НЬ ГОЛ ЗАСВАР. Өмнө нь эцэг ангилал сонгоход backend рүү шүүлт
  //    явуулдаггүй, ачаалсан 32 мөрөө УРДАА шүүдэг байв. Бараа нь мянган
  //    мөрийн дундуур тархсан тул (prod 2026-10-27 хэмжилт):
  //      • 51 эцэг ангилал «Бараа байхгүй байна» гэж ХУДЛАА харуулдаг байсан
  //        (1,133 бараа нуугдсан) — `items.length === 0` тул хүснэгт ч гардаггүй;
  //      • 26 эцэг ангилал гүйлгэхэд `dataLength` зогсож, infinite-scroll
  //        МӨНХӨД түгждэг байв (сангийн `actionTriggered` зөвхөн `dataLength`
  //        өөрчлөгдөхөд л reset хийгддэг).
  //    Одоо эцэг + бүх үр садын id-г backend рүү явуулж, шүүлтийг СЕРВЕР хийнэ.
  const categoryIds = useMemo(() => {
    const target = selectedSubCategory || selectedCategory;
    if (!target) return [];
    const node = findNode(rawCategories, target);
    return node ? collectIds(node) : [Number(target)];
  }, [selectedCategory, selectedSubCategory, rawCategories]);

  // Массив нь render бүрт шинэ reference болдог тул effect-ийн хамаарлыг
  // тогтвортой мөрөөр өгнө.
  const categoryKey = categoryIds.join(",");

  const load = useCallback(
    async (pageToLoad, mode /* "refresh" | "more" */) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const reqId = ++reqIdRef.current;
      if (mode === "refresh") {
        setLoading(true);
        setLoadError(false);
        setLoadMoreError(false);
      } else {
        setLoadingMore(true);
        setLoadMoreError(false);
      }

      try {
        const result = await fetchProducts({
          page: pageToLoad,
          pageSize: PAGE_SIZE,
          categoryIds,
          keyword: search || undefined,
          isVisible: visibility ?? undefined,
        });

        // Хуучирсан хариу — хаяна (хайлтын уралдааны хамгаалалт).
        if (reqId !== reqIdRef.current) return;

        const chunk = Array.isArray(result?.data) ? result.data : [];
        const pg = result?.pagination || {};

        setItems((prev) =>
          mode === "refresh" ? chunk : dedupeById([...prev, ...chunk])
        );
        pageRef.current = pg.currentPage || pageToLoad;
        setTotal(pg.totalItems || 0);
        setHasMore((pg.currentPage || pageToLoad) < (pg.totalPages || 0));
      } catch (error) {
        if (reqId !== reqIdRef.current) return;
        console.error("Fetch products error:", error);
        if (mode === "refresh") {
          setItems([]);
          setHasMore(false);
          setLoadError(true); // сүлжээ/серверийн алдаа — «дата алга» биш
        } else {
          // ⚠️ Өмнө нь энд ЮУ Ч хийдэггүй байсан: `items` өөрчлөгдөхгүй тул
          //    infinite-scroll-ийн `actionTriggered` мөнхөд `true` үлдэж,
          //    спиннер эргэлдсээр жагсаалт үхдэг байв. Одоо алдааг харуулж,
          //    «Дахин оролдох» товч өгнө.
          setLoadMoreError(true);
        }
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
        inFlightRef.current = false;
      }
    },
    [categoryKey, search, visibility] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    loadCategories();
  }, []);

  // ⚠️ Өмнө нь mount дээр ХОЁР effect хоёулаа 1-р хуудсыг татдаг байсан —
  //    хоёр ижил хүсэлт зэрэг явж, тааруухан дарааллаар ирвэл нэг хуудас
  //    бараа чимээгүй алдагддаг байв. Одоо ганц эх сурвалж.
  useEffect(() => {
    pageRef.current = 1;
    load(1, "refresh");
  }, [load]);

  const handleLoadMore = () => {
    if (!hasMore || inFlightRef.current || loadMoreError) return;
    load(pageRef.current + 1, "more");
  };

  const handleRetryMore = () => {
    setLoadMoreError(false);
    load(pageRef.current + 1, "more");
  };

  const handleVisible = async (e, productId) => {
    const newVisible = e.target.checked;
    try {
      setLoadingProductId(productId);
      const res = await updateProduct(productId, { isVisible: newVisible });
      // {success:false} хариуг амжилттай гэж харуулахгүй.
      if (res?.success === false) throw new Error(res?.message || "Алдаа");

      setItems((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, isVisible: newVisible } : p))
      );

      toast.success(res.message || "Бараа амжилттай шинэчлэгдлээ.");
    } catch (error) {
      console.error("Product visible update error:", error);
      toast.error("Барааг шинэчлэх үед алдаа гарлаа.");
    } finally {
      setLoadingProductId(null);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    openModal("edit-product");
  };

  const handleSaveProduct = async (updatedData) => {
    if (!editingProduct?.id) {
      toast.error("Барааны ID олдсонгүй.");
      return;
    }

    try {
      setSaving(true);
      setLoadingProductId(editingProduct.id);

      const res = await updateProduct(editingProduct.id, updatedData);

      setItems((prev) =>
        prev.map((p) => (p.id === editingProduct.id ? { ...p, ...res.product } : p))
      );

      toast.success(res.message || "Бараа амжилттай шинэчлэгдлээ.");
    } catch (error) {
      console.error("Product update error:", error);
      toast.error("Барааг шинэчлэх үед алдаа гарлаа.");
    } finally {
      setSaving(false);
      setLoadingProductId(null);
      closeModal();
      setEditingProduct(null);
    }
  };

  const handleRefetch = () => {
    pageRef.current = 1;
    load(1, "refresh");
  };

  const isEmpty = !loading && items.length === 0;

  const handleDeleteProduct = (product) => {
    setDeletingProduct(product);
    openModal("delete-product");
  };

  const confirmDeleteProduct = async () => {
    if (!deletingProduct?.id) {
      toast.error("Устгах барааны ID олдсонгүй.");
      return;
    }
    try {
      setDeleting(true);
      const res = await deleteProduct(deletingProduct.id);
      if (res?.success === false) throw new Error(res?.message || "Алдаа");

      setItems((prev) => prev.filter((p) => p.id !== deletingProduct.id));
      setTotal((t) => Math.max(0, t - 1));

      toast.success("Бараа амжилттай устгагдлаа.");
    } catch (error) {
      console.error(error);
      toast.error("Барааг устгах үед алдаа гарлаа.");
    } finally {
      setDeleting(false);
      setDeletingProduct(null);
      closeModal();
    }
  };

  return (
    <div className="container-fluid app">
      <div className="row">
        <div className="col-12">
          <Toolbar
            search={search}
            setSearch={setSearch}
            selectedCategory={selectedCategory}
            handleCategoryChange={(v) => setSelectedCategory(v)}
            selectedSubCategory={selectedSubCategory}
            handleSubCategoryChange={(v) => setSelectedSubCategory(v)}
            visibility={visibility}
            handleVisibilityChange={(v) => setVisibility(v)}
            categories={categories}
            rawCategories={rawCategories}
            loadingCategories={loadingCategories}
            categoriesError={categoriesError}
            onRefetch={handleRefetch}
          />
          <h4 className="page-title mt-3">
            Бараа засах
            {total > 0 && (
              <small className="text-muted ms-2" style={{ fontSize: 13 }}>
                ({items.length} / {total})
              </small>
            )}
          </h4>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body pt-1">
              {loadError && !loading && items.length === 0 ? (
                <div className="text-center py-5">
                  <i className="uil uil-exclamation-triangle text-danger" style={{ fontSize: 40 }} />
                  <p className="mt-2 mb-3">Бараа татахад алдаа гарлаа.</p>
                  <button className="btn btn-outline-primary" onClick={handleRefetch}>
                    Дахин оролдох
                  </button>
                </div>
              ) : isEmpty ? (
                <EmptyStateComponent
                  title={"Бараа байхгүй байна"}
                  subtitle={
                    search || categoryIds.length || visibility !== null
                      ? "Энэ шүүлтүүрт тохирох бараа олдсонгүй"
                      : "Та одоогоор ямар нэг бараа бүртгээгүй байна"
                  }
                />
              ) : (
                <ProductTable
                  items={items}
                  loading={loading}
                  loadingMore={loadingMore}
                  hasMore={hasMore}
                  loadMoreError={loadMoreError}
                  onEndReached={handleLoadMore}
                  onRetryMore={handleRetryMore}
                  loadingProductId={loadingProductId}
                  handleVisible={handleVisible}
                  onEdit={handleEditProduct}
                  onDelete={handleDeleteProduct}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <EditProductsModal
        show={currentModal === "edit-product"}
        handleClose={() => {
          closeModal();
          setEditingProduct(null);
        }}
        initialData={editingProduct || {}}
        categories={rawCategories}
        onSave={handleSaveProduct}
        saving={saving}
      />

      <DeleteProductModal
        show={currentModal === "delete-product"}
        handleClose={() => {
          closeModal();
          setDeletingProduct(null);
        }}
        product={deletingProduct || {}}
        onDelete={confirmDeleteProduct}
        deleting={deleting}
      />

      {showScrollButton && <ScrollToTopButton onClick={scrollToTop} />}
    </div>
  );
}
